import produce from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { types } from './types';
import type { IMessageInitialState } from './types';

const defaultPagination = {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20,
};

const initialState: IMessageInitialState = {
    users: [],
    rooms: [],
    selectedRoomMessages: [],
    roomMsgError: null,
    fetchingRoomMsg: false,
    fetchingRooms: false,
    fetchingMessages: false,
    sendingMessage: false,
    gettingMessages: false,
    gettingMessagesError: null,
    isUserTyping: 'no',
    newMessage: false,
    sendingMsgError: null,
    onlineUsers: [],
    newContentAvailable: false,
    newContentType: '',
    roomsPagination: defaultPagination,
    messagesPagination: defaultPagination,
    searching: false,
    searchError: null,
    isSearchActive: false,
    searchTerm: '',
    fetchingMoreRooms: false,
    fileUploadQueue: [],
    /** Participants of the selected room */
    participants: [],
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case types.RETRIEVE_ROOMS:
            return produce(state, draftState => {
                draftState.rooms = [];
                draftState.fetchingRooms = true;
            });
        case types.GET_MY_ROOMS:
            return produce(state, draftState => {
                const pageNum = payload?.page ?? 1;
                if (pageNum < 2) {
                    // Keep existing rooms visible while refetching (avoids list / pinned row flicker).
                    draftState.fetchingRooms = true;
                } else {
                    draftState.fetchingMoreRooms = true;
                }
            });
        case types.GET_ALL_MESSAGES:
            return produce(state, draftState => {
                draftState.gettingMessages = true;
            });
        case types.GET_ALL_MESSAGES_SUCCESS:
            return produce(state, draftState => {
                const { rooms, pagination } = payload;
                if (pagination.currentPage === 1) {
                    draftState.rooms = rooms;
                    draftState.fetchingRooms = false;
                } else {
                    draftState.rooms = [...draftState.rooms, ...rooms];
                    draftState.fetchingMoreRooms = false;
                }
                draftState.roomsPagination = pagination;
            });
        case types.GET_MY_ROOMS_SUCCESS:
            return produce(state, draftState => {
                const { rooms, pagination } = payload;
                const incoming = rooms ?? [];
                const prev = draftState.rooms ?? [];

                if (pagination.currentPage === 1) {
                    // Avoid wiping the sidebar when the server/socket sends [] or a partial list while
                    // another action (e.g. opening a chat) is in flight.
                    if (incoming.length === 0 && prev.length > 0) {
                        draftState.fetchingRooms = false;
                    } else if (incoming.length > 0 && incoming.length < prev.length && prev.length > 0) {
                        const byId = new Map(prev.map(r => [r._id, r]));
                        for (const r of incoming) {
                            byId.set(r._id, r);
                        }
                        draftState.rooms = Array.from(byId.values());
                        draftState.fetchingRooms = false;
                    } else {
                        draftState.rooms = incoming;
                        draftState.fetchingRooms = false;
                    }
                } else {
                    draftState.rooms = [...draftState.rooms, ...incoming];
                    draftState.fetchingMoreRooms = false;
                }
                draftState.roomsPagination = pagination;
            });
        case types.GET_ALL_MESSAGES_FAIL:
        case types.GET_MY_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.gettingMessagesError = payload;
                draftState.fetchingRooms = false;
                draftState.fetchingMoreRooms = false;
            });
        case types.SET_ROOM_MESSAGES:
            return produce(state, draftState => {
                // Only clear messages if it's the first page
                if (!payload.pagination || payload.pagination.currentPage === 1) {
                    // draftState.selectedRoomMessages = [];
                    // draftState.fetchingRoomMsg = true;
                    // draftState.loadingMore = false
                }
            });
        case types.SET_ROOM_MESSAGES_SUCCESS:
            return produce(state, draftState => {
                const { messages, pagination, participants } = payload;
                const sortedMessageArray = _.orderBy(messages, (m: any) => moment(m.createdAt), ['desc']);

                // If it's the first page, replace messages. Otherwise, append
                if (pagination.currentPage === 1) {
                    draftState.selectedRoomMessages = sortedMessageArray;
                } else {
                    draftState.selectedRoomMessages = [...draftState.selectedRoomMessages, ...sortedMessageArray];
                }
                draftState.messagesPagination = pagination;
                draftState.fetchingMessages = false;
                draftState.loadingMore = false;
                draftState.participants = participants;
            });
        case types.SET_ROOM_MESSAGES_FAIL:
            return produce(state, draftState => {
                draftState.fetchingRoomMsg = false;
                draftState.roomMsgError = payload;
                draftState.fetchingMessages = false;
            });
        case types.USER_TYPING:
        case types.USER_STOP_TYPING:
            return produce(state, draftState => {
                const { status, roomID, userId } = payload;
                draftState.isUserTyping = status;
                const findIndexForMsg = draftState.rooms.findIndex(room => room._id === roomID);
                if (findIndexForMsg < 0) {
                    return;
                }
                draftState.rooms[findIndexForMsg].isUserTyping = status;
                const userTypingObject = draftState.rooms[findIndexForMsg]?.userTyping ?? [];
                if (status === 'yes') {
                    draftState.rooms[findIndexForMsg].userTyping = [...userTypingObject, userId];
                } else {
                    draftState.rooms[findIndexForMsg].userTyping = draftState.rooms[findIndexForMsg].userTyping?.filter(
                        id => String(id) !== String(userId),
                    );
                }
            });
        case types.SEND_MESSAGE:
            return produce(state, draftState => {
                draftState.newMessage = false;
                const messageObject = {
                    ...payload,
                    created_by: payload.userObject,
                    _id: payload.uniqueSocketID,
                    parent_message: payload.replyingToObject,
                    is_reply: !!payload.replyingToObject,
                    // Add properly formatted attachment data based on message type
                    ...(payload.message_type === 'picture_message' && {
                        image: payload.image || [],
                        isLoading: true,
                    }),
                    ...(payload.message_type === 'document_message' && {
                        document: payload.document || [],
                        isLoading: true,
                    }),
                    ...(payload.message_type === 'video_message' && {
                        media: payload.tempProjects || [],
                        tempProjects: payload.tempProjects || [],
                    }),
                    ...(payload.message_type === 'sound_message' && {
                        audio: payload.tempAudios || [],
                        tempAudios: payload.tempAudios || [],
                    }),
                    ...(payload.message_type === 'location_message' && {
                        location: payload.location || {},
                    }),
                };

                const sendRoomId = typeof payload.room === 'string' ? payload.room : payload.room?._id;
                const selectedRoomId =
                    draftState.selectedRoomMessages.length > 0
                        ? typeof draftState.selectedRoomMessages[0].room === 'string'
                            ? draftState.selectedRoomMessages[0].room
                            : draftState.selectedRoomMessages[0].room?._id
                        : null;

                if (sendRoomId && (selectedRoomId === null || selectedRoomId === sendRoomId)) {
                    draftState.selectedRoomMessages = [messageObject, ...draftState.selectedRoomMessages];
                }

                // Inbox / floating list: show latest snippet immediately (before socket echo)
                const roomIdx = sendRoomId ? draftState.rooms.findIndex(r => r._id === sendRoomId) : -1;
                if (roomIdx > -1) {
                    const roomDraft = draftState.rooms[roomIdx];
                    const optimisticForList = {
                        ...messageObject,
                        room: roomDraft,
                    } as any;
                    if (!roomDraft.messages) {
                        roomDraft.messages = [];
                    }
                    roomDraft.messages.unshift(optimisticForList);
                    roomDraft.last_message_at = payload.createdAt || new Date().toISOString();
                }
            });
        case types.SEND_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const successRoomId = typeof payload?.room === 'string' ? payload.room : payload?.room?._id;
                /** get The ID of the message */
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    msg => msg.uniqueSocketID === payload.uniqueSocketID,
                );
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = payload;
                } else if (successRoomId && draftState.selectedRoomMessages.length > 0) {
                    const firstRoom = draftState.selectedRoomMessages[0]?.room;
                    const firstRoomId = typeof firstRoom === 'string' ? firstRoom : firstRoom?._id;
                    if (firstRoomId === successRoomId) {
                        draftState.selectedRoomMessages.unshift(payload);
                    }
                }

                const findIndexForMsg: number = successRoomId
                    ? draftState.rooms.findIndex(room => room._id === successRoomId)
                    : -1;

                if (findIndexForMsg > -1) {
                    const roomDraft = draftState.rooms[findIndexForMsg];
                    if (!roomDraft.messages) {
                        roomDraft.messages = [];
                    }
                    const listIdx = roomDraft.messages.findIndex(
                        m => m.uniqueSocketID === payload.uniqueSocketID || m._id === payload.uniqueSocketID,
                    );
                    if (listIdx > -1) {
                        roomDraft.messages[listIdx] = payload;
                    } else {
                        roomDraft.messages.unshift(payload);
                    }

                    if (payload.newRoom?.unread_message_count !== undefined) {
                        roomDraft.unread_message_count = payload.newRoom.unread_message_count;
                    }
                    if (payload.newRoom) {
                        roomDraft.read_by = payload.newRoom.read_by ?? roomDraft.read_by;
                        roomDraft.last_message_at =
                            payload.newRoom.last_message_at ?? roomDraft.last_message_at ?? payload.createdAt;
                    }
                } else if (payload.newRoom && successRoomId) {
                    // Receiver path: if this room is not yet in local list, insert it immediately
                    // so floating/inbox modals reflect the new conversation without waiting for a refresh.
                    draftState.rooms.unshift(payload.newRoom as any);
                }

                draftState.newMessage = true;
            });
        case types.SEND_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.newMessage = false;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    msg => msg.uniqueSocketID === payload.uniqueSocketID,
                );
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages = draftState.selectedRoomMessages.filter(
                        (_, index) => index !== messageIndex,
                    );
                }
                const failedBody = payload?.payload ?? payload;
                const failRoomId = typeof failedBody?.room === 'string' ? failedBody.room : failedBody?.room?._id;
                const failRoomIdx = failRoomId ? draftState.rooms.findIndex(r => r._id === failRoomId) : -1;
                if (failRoomIdx > -1 && draftState.rooms[failRoomIdx].messages?.length) {
                    draftState.rooms[failRoomIdx].messages = draftState.rooms[failRoomIdx].messages.filter(
                        m => m.uniqueSocketID !== payload.uniqueSocketID && m._id !== payload.uniqueSocketID,
                    );
                }
            });
        case types.INITIATE_MESSAGE:
            return produce(state, draftState => {
                draftState.sendingMessage = true;
            });
        case types.INITIATE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingMessage = false;

                const roomIndex = draftState.rooms.findIndex(room => room._id === payload._id);

                if (roomIndex > -1) {
                    // Replace existing room
                    draftState.rooms[roomIndex] = payload;
                } else {
                    // Add new room to the top
                    draftState.rooms.unshift(payload);
                }

                // Update selectedRoomMessages if this is the current room
                if (draftState.selectedRoomMessages[0]?.room?._id === payload._id) {
                    // Ensure no duplicate message IDs in selectedRoomMessages
                    const firstMessageId = payload.messages[0]?._id;
                    const exists = draftState.selectedRoomMessages.some(msg => msg._id === firstMessageId);

                    if (!exists && payload.messages.length > 0) {
                        draftState.selectedRoomMessages.unshift(payload.messages[0]);
                    }
                }
            });

        case types.INITIATE_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.sendingMsgError = payload;
            });
        case types.SET_ONLINE_USERS:
            return produce(state, draftState => {
                draftState.onlineUsers = [...new Set([...draftState.onlineUsers, payload])] as any;
            });
        case types.SET_OFFLINE_USER:
            return produce(state, draftState => {
                draftState.onlineUsers = draftState.onlineUsers.filter(user => user !== payload);
            });
        case types.NEW_CONTENT_AVAILABLE:
            return produce(state, draftState => {
                draftState.newContentAvailable = true;
                draftState.newContentType = payload;
            });
        case types.RESET_NEW_CONTENT:
            return produce(state, draftState => {
                draftState.newContentAvailable = false;
                draftState.newContentType = '';
            });
        case types.RETRIEVE_USER_ROOMS:
            return produce(state, draftState => {
                const {
                    data: {
                        query: { page },
                    },
                } = payload;
                if (page && page < 2) {
                    draftState.fetchingRooms = true;
                }
            });
        case types.RETRIEVE_USER_ROOMS_SUCCESS:
            return produce(state, draftState => {
                draftState.fetchingRooms = false;
            });
        case types.RETRIEVE_USER_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.fetchingRooms = false;
            });
        case 'LOGOUT':
        case 'LOGOUT_SUCCESS':
            return {
                ...initialState,
                roomsPagination: { ...defaultPagination },
                messagesPagination: { ...defaultPagination },
            };
        case types.GET_MESSAGES_FOR_SELECTED_ROOM:
            return produce(state, draftState => {
                if (payload.pagination.page < 2) {
                    draftState.fetchingMessages = true;
                } else {
                    draftState.loadingMore = true;
                }
                draftState.participants = [];
            });
        case types.UPDATE_UNREAD_COUNT:
            return produce(state, draftState => {
                const { roomID, readBy } = payload;
                if (roomID && readBy) {
                    const selectedRoomIndex = draftState.rooms.findIndex(room => room._id === roomID);
                    if (selectedRoomIndex > -1) {
                        const room = draftState.rooms[selectedRoomIndex];

                        if (!room.unread_message_count) {
                            room.unread_message_count = [];
                        }

                        const userIndex = room.unread_message_count.findIndex(u => u.user === readBy);

                        if (userIndex > -1) {
                            // Replace existing user entry with count 0
                            room.unread_message_count[userIndex] = {
                                user: readBy,
                                count: 0,
                            };
                        } else {
                            // Add new entry
                            room.unread_message_count.push({
                                user: readBy,
                                count: 0,
                            });
                        }
                    }
                }
            });
        case types.UPDATE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { currentRoom, updatedMessage } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    value => value._id === updatedMessage._id,
                );
                const roomIndex = draftState.rooms.findIndex(room => room._id === currentRoom._id);
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = updatedMessage;
                }
                if (roomIndex > -1) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages ?? [];
                    const messageIndexForRoom = currentRoomMessages.findIndex(
                        value => value._id === updatedMessage._id,
                    );
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = updatedMessage;
                    } else {
                        currentRoomMessages.unshift(updatedMessage);
                    }
                }
            });
        case types.REACT_TO_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { room, updatedMessage } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    value => value._id === updatedMessage._id,
                );
                const roomIndex = draftState.rooms.findIndex(r => r._id === room);
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = updatedMessage;
                }
                if (roomIndex > -1) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages ?? [];
                    const messageIndexForRoom = currentRoomMessages.findIndex(
                        value => value._id === updatedMessage._id,
                    );
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = updatedMessage;
                    } else {
                        currentRoomMessages.unshift(updatedMessage);
                    }
                }
            });
        case types.DELETE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { currentRoom, messageId } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(value => value._id === messageId);
                const roomIndex = draftState.rooms.findIndex(room => room._id === currentRoom._id);
                const originalMessage = draftState.selectedRoomMessages[messageIndex];
                const deletedMessageBody = {
                    ...originalMessage,
                    deleted: true,
                };
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = deletedMessageBody;
                }
                if (roomIndex > -1 && draftState.rooms[roomIndex]?.messages) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages;
                    const messageIndexForRoom = currentRoomMessages.findIndex(value => value._id === messageId);
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = deletedMessageBody;
                    }
                }
            });
        case types.SEARCH_ROOMS:
            return produce(state, draftState => {
                draftState.searching = true;
                draftState.searchError = null;
                draftState.isSearchActive = true;
                draftState.searchTerm = payload.searchTerm;
            });
        case types.SEARCH_ROOMS_SUCCESS:
            return produce(state, draftState => {
                const { rooms, pagination } = payload;

                // Create a map of existing rooms by ID for quick reference
                const existingRoomsMap = new Map(draftState.rooms.map(room => [room._id, room]));

                // Process search results, preserving user data from existing rooms
                const processedRooms = rooms.map((room: any) => {
                    const existingRoom = existingRoomsMap.get(room._id);
                    if (existingRoom) {
                        // Preserve user data and important fields from the existing room
                        return {
                            ...existingRoom,
                            ...room,
                            // Ensure these fields are preserved to maintain ConversationView functionality
                            sender: existingRoom.sender || room.sender,
                            receiver: existingRoom.receiver || room.receiver,
                            messages: room.messages || existingRoom.messages,
                            matchReason: room.matchReason,
                            last_message_at: room.last_message_at || existingRoom.last_message_at,
                        };
                    }
                    return room;
                });

                // If it's the first page, replace rooms with processed results. Otherwise, append
                if (pagination.currentPage === 1) {
                    draftState.rooms = processedRooms;
                } else {
                    // For subsequent pages, we need to avoid duplicating rooms
                    const existingIds = new Set(draftState.rooms.map(r => r._id));
                    const newRooms = processedRooms.filter((room: any) => !existingIds.has(room._id));
                    draftState.rooms = [...draftState.rooms, ...newRooms];
                }

                draftState.roomsPagination = pagination;
                draftState.searching = false;
                draftState.fetchingRooms = false;
            });
        case types.SEARCH_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.searching = false;
                draftState.searchError = payload;
            });
        case types.QUEUE_FILE_UPLOAD:
            return produce(state, draftState => {
                // Ensure fileUploadQueue is an array
                if (!draftState.fileUploadQueue || !Array.isArray(draftState.fileUploadQueue)) {
                    draftState.fileUploadQueue = [];
                }
                draftState.fileUploadQueue.push(payload);
            });
        case types.UPLOAD_MESSAGE_FILE_SUCCESS:
            return produce(state, draftState => {
                const { messageId, fileUrls } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(msg => msg._id === messageId);

                if (messageIndex > -1) {
                    const message = draftState.selectedRoomMessages[messageIndex];

                    // Update appropriate field based on message type
                    if (message.message_type === 'picture_message') {
                        // Append to image array
                        if (!message.image) message.image = [];
                        message.image = fileUrls;
                        message.isLoading = false;
                    } else if (message.message_type === 'document_message') {
                        // Append to document array
                        if (!message.document) message.document = [];
                        message.document = fileUrls;
                        message.isLoading = false;
                    }

                    // Remove from queue - ensure it's an array before filtering
                    if (draftState.fileUploadQueue && Array.isArray(draftState.fileUploadQueue)) {
                        draftState.fileUploadQueue = draftState.fileUploadQueue.filter(
                            item => item.messageId !== messageId,
                        );
                    } else {
                        draftState.fileUploadQueue = [];
                    }
                }
            });
        case types.UPLOAD_MESSAGE_FILE_FAIL:
            return produce(state, draftState => {
                const { messageId } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(msg => msg._id === messageId);

                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex].uploadError = true;
                    draftState.selectedRoomMessages[messageIndex].isLoading = false;
                }

                // Remove from queue - ensure it's an array before filtering
                if (draftState.fileUploadQueue && Array.isArray(draftState.fileUploadQueue)) {
                    draftState.fileUploadQueue = draftState.fileUploadQueue.filter(
                        item => item.messageId !== messageId,
                    );
                } else {
                    draftState.fileUploadQueue = [];
                }
            });
        default:
            return state;
    }
};

export { reducer };
